/**
 * The header bar's download icon (VE-26426).
 *
 * The header is the aiWARE SDK's `APP_BAR` widget, not part of this app's React
 * tree: its `rightActions` take raw HTML strings and are captured when
 * `mountWidget` runs. So the icon cannot be added when a download starts and
 * removed when it ends, and it cannot re-render from React state — it is mounted
 * for the life of the app and driven by DOM writes, as `notificationBadge.tsx`
 * does for the notification count.
 *
 * Those writes cannot be one-shot. The SDK keeps the markup string it was handed
 * and re-injects it whenever it re-renders the bar — and that string was built
 * before any download existed, so it says hidden. A React effect keyed on the
 * state does not fire for that: the state did not change, the DOM did. The icon
 * really did vanish a couple of seconds into a download this way.
 *
 * Hence: the desired state is held here, `buildDownloadIconMarkup` bakes it into
 * every rebuild, and `applyDownloadIconState` re-asserts it against whatever is
 * in the DOM. `watchDownloadIcon` runs that on every DOM change, so the icon
 * converges on the truth no matter what the widget does to the node.
 *
 * Its tooltip is not here: that is a MUI `<Tooltip>` anchored to this node from
 * the React tree, so it matches every other tooltip in the app rather than being
 * the browser's native one. See HeaderIconTooltip. The accessible name goes on
 * this node too, from there — a MUI tooltip cannot supply one, so the markup
 * below carrying no text means something else has to.
 */
import { observeWidgetDom } from './widgetDomWatcher';

/**
 * The arrow's size, in px, idle and while the ring turns.
 *
 * Chrome shrinks its download glyph once a transfer starts so the spinner has
 * somewhere to go; at one fixed size the ring either crowds the arrow or the
 * arrow has to be drawn small enough to look weedy when nothing is happening.
 */
const ARROW_SIZE_IDLE = 20;
const ARROW_SIZE_SPINNING = 13;

/**
 * Exported because two other places need to name this node: the tooltip anchors
 * to it, and the tray's click-away has to exempt it. Three copies of the string
 * was one too many.
 */
export const DOWNLOAD_ACTION_ID = 'downloadTrayAction';
const DOWNLOAD_BUTTON_ID = 'downloadTrayButton';
const DOWNLOAD_RING_ID = 'downloadTrayRing';
const DOWNLOAD_ARROW_ID = 'downloadTrayArrow';

export interface DownloadIconState {
  /** A download session exists, so the icon is on the bar at all. */
  readonly visible: boolean;
  /** Bytes are actually moving, so the ring turns. */
  readonly spinning: boolean;
}

/**
 * Module-level because it mirrors a single DOM node the SDK owns and recreates
 * behind our back — there is one header, and this is what it should be showing.
 * Kept private; the two functions below are the only way in and out.
 */
let desiredState: DownloadIconState = { visible: false, spinning: false };

const displayFor = (shown: boolean) => (shown ? 'block' : 'none');

const arrowSize = () =>
  desiredState.spinning ? ARROW_SIZE_SPINNING : ARROW_SIZE_IDLE;

/**
 * Inlined rather than imported from `resources/images` so the markup stays
 * self-contained — this string is handed to the SDK, not rendered by React, so
 * it cannot carry a webpack-resolved asset URL through a component.
 */
const DOWNLOAD_ARROW_PATH = 'M5 20h14v-2H5v2zM19 9h-4V3H9v6H5l7 7 7-7z';

/**
 * Every value in here is a constant of this module or a JSS-generated class
 * name; nothing user-supplied reaches it. There is no text at all — the tooltip
 * is a MUI `<Tooltip>` anchored to this node from the React tree, so that it
 * looks and behaves like every other tooltip in the app. See HeaderIconTooltip.
 */
export const buildDownloadIconMarkup = (classes: {
  downloadBtn: string;
  downloadIcon: string;
  downloadRing: string;
}) => `
    <div id="${DOWNLOAD_ACTION_ID}">
      <span id="${DOWNLOAD_BUTTON_ID}" class="${classes.downloadBtn}" style="display: ${displayFor(
        desiredState.visible
      )}">
        <span id="${DOWNLOAD_RING_ID}" class="${classes.downloadRing}" style="display: ${displayFor(
          desiredState.spinning
        )}"></span>
        <svg id="${DOWNLOAD_ARROW_ID}" class="${
          classes.downloadIcon
        }" viewBox="0 0 24 24" aria-hidden="true" width="${arrowSize()}" height="${arrowSize()}">
          <path d="${DOWNLOAD_ARROW_PATH}" />
        </svg>
      </span>
    </div>
  `;

/**
 * Write the desired state onto whatever node is in the DOM right now. Safe to
 * call as often as you like: it is two lookups and two assignments, and it is
 * what makes a rebuilt node correct again.
 */
export const applyDownloadIconState = () => {
  const button = document.querySelector<HTMLElement>(`#${DOWNLOAD_BUTTON_ID}`);
  if (button) {
    button.style.display = displayFor(desiredState.visible);
  }
  const ring = document.querySelector<HTMLElement>(`#${DOWNLOAD_RING_ID}`);
  if (ring) {
    ring.style.display = displayFor(desiredState.spinning);
  }
  const arrow = document.querySelector<SVGElement>(`#${DOWNLOAD_ARROW_ID}`);
  if (arrow) {
    arrow.setAttribute('width', String(arrowSize()));
    arrow.setAttribute('height', String(arrowSize()));
  }
};

/** Record what the icon should show, and put it on screen. */
export const setDownloadIconState = (next: DownloadIconState) => {
  desiredState = next;
  applyDownloadIconState();
};

/**
 * Re-assert the state whenever the widget rebuilds its DOM.
 *
 * The SDK keeps the markup string it was handed at `mountWidget` and re-injects
 * it whenever it re-renders the bar — and that string was built before any
 * download existed, so it says `display: none`. A couple of seconds after
 * startup the SDK does exactly that, and the icon vanished mid-download.
 *
 * Watching the DOM rather than polling, because the poll that looked like it
 * would cover this stops after the first tick (AppBar clears its interval once
 * aiWARE has initialised), and no React effect fires either: the state did not
 * change, the DOM did.
 *
 * Through the shared watcher rather than an observer of its own: the two header
 * tooltips want the same signal, and one observer answering all three is a third
 * of the work for the same event. Only `childList` is observed there, so writing
 * `style` here cannot retrigger it. Returns a disposer.
 */
export const watchDownloadIcon = (): (() => void) =>
  observeWidgetDom(applyDownloadIconState);

/** Test seam — the module state outlives a single test otherwise. */
export const resetDownloadIconState = () => {
  desiredState = { visible: false, spinning: false };
};
